import type { SingleCommands } from '@tiptap/core';
import { findChildren, findParentNodeClosestToPos } from '@tiptap/core';
import type { Node, ResolvedPos } from '@tiptap/pm/model';
import type { EditorState } from '@tiptap/pm/state';
import { TIPTAP_COLLAPSIBLE_LIST_TYPE } from 'ricos-content';
import { getFragmentContentNodes } from 'ricos-plugins';

import type { CollapsibleListComponentData } from '../types';

export type CollapsibleListAttributes = Pick<
  CollapsibleListComponentData,
  'expandOnlyOne' | 'initialExpandedItems'
>;

export type CollapsibleItemAttributes = { isExpanded: boolean };

export function updateCollapsibleListAttrs(
  commands: SingleCommands,
  nodeId: string,
  attrs: CollapsibleListAttributes
) {
  commands.updateNodeAttrsById(nodeId, attrs);
}

export function updateCollapsibleListItemsAttrs(
  state: EditorState,
  commands: SingleCommands,
  nodeId: string,
  { initialExpandedItems }: CollapsibleListAttributes
) {
  const collapsibleListNode = findNodeById(state, nodeId)[0].node;

  const nodes = getFragmentContentNodes(collapsibleListNode.content);

  nodes.forEach((node, index) => {
    const isExpanded =
      initialExpandedItems === 'ALL' || (index === 0 && initialExpandedItems === 'FIRST');

    updateListItemAttributes(commands, node, {
      isExpanded,
    });
  });
}

export function updateListItemAttributes(
  commands: SingleCommands,
  node: Node,
  attrs: CollapsibleItemAttributes
) {
  commands.updateNodeAttrsById(node.attrs.id, attrs);
  const nodeContent = node.content;

  if (
    !!nodeContent &&
    'content' in nodeContent &&
    Array.isArray(nodeContent.content) &&
    nodeContent.content[1]
  ) {
    commands.updateNodeAttrsById(nodeContent.content[1].attrs.id, attrs); // used to trigger rerender on list item body
  }
}

export function isInCollapsibleList($pos: ResolvedPos, nodeId: string) {
  const collapsibleListNode = findParentNodeClosestToPos(
    $pos,
    node => node.type?.name === TIPTAP_COLLAPSIBLE_LIST_TYPE
  )?.node;
  const collapsibleListNodeContent = collapsibleListNode?.content;
  if (!collapsibleListNodeContent) {
    return false;
  }

  const fragmentNodes = getFragmentContentNodes(collapsibleListNodeContent);
  return fragmentNodes.some(node => node.attrs.id === nodeId);
}

export function findCollapsibleListById(state: EditorState, nodeId: string) {
  let collapsibleList: Node | undefined;
  state.doc.descendants(node => {
    if (node.attrs.id === nodeId) {
      collapsibleList = node;
    }
  });
  return collapsibleList;
}

export function getCollapsibleListItems(state: EditorState, nodeId: string) {
  const collapsibleListNode = findCollapsibleListById(state, nodeId);
  if (collapsibleListNode?.type.name === TIPTAP_COLLAPSIBLE_LIST_TYPE) {
    return getFragmentContentNodes(collapsibleListNode.content);
  }
  return [];
}

export function getCollapsibleListByItemId(state: EditorState, nodeId: string) {
  const collapsibleLists = findNodesByType(state, TIPTAP_COLLAPSIBLE_LIST_TYPE);

  let collapsibleList: Node | undefined;
  collapsibleLists.forEach(listNode => {
    listNode.node.descendants(node => {
      if (node.attrs.id === nodeId) {
        collapsibleList = listNode.node;
        return;
      }
    });
  });

  return collapsibleList;
}

export const findNodesByType = (state: EditorState, type: string) => {
  return findChildren(state.doc, node => node.type.name === type);
};

export const findNodeById = (state: EditorState, id: string) => {
  return findChildren(state.doc, node => node.attrs.id === id);
};
